import { ref } from 'vue'
import { useUserStore } from '@/lib/stores'
import { FeatureConfig } from '@/lib/services'
import {FREE_TRIAL_ALERT, FEATURE_LIMIT_REACHED_ALERT} from '@/lib/utils/constants'

export interface AlertModalData {
  show: boolean
  key: string
  count: number
  message: string
  title: string
  subtitle: string
  description: string
  /** Show the dimmed backdrop (scrim) behind the alert. Off for the standard feature-limit popup so the page stays clickable. */
  showBackdrop: boolean
}

export type FeatureWithPackageMeta = FeatureConfig & {
  product_name?: string
  start_date?: string
  renewal_date?: string
  subscription_type?: string
}

// sessionStorage is scoped to the browser tab, so a dismissal lasts for this tab session only.
const DISMISSED_LIMIT_ALERT_PAGES_KEY = 'limit_reached_alert_dismissed_pages'

const getCurrentPage = (): string => window.location.pathname.replace(/\/+$/, '') || '/'

const getDismissedPages = (): string[] => {
  try {
    const raw = sessionStorage.getItem(DISMISSED_LIMIT_ALERT_PAGES_KEY)
    const parsed = raw ? JSON.parse(raw) : []
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

export const AlertModalService = () => {
  const packageFeatures = ref<FeatureWithPackageMeta[]>([])
  // True when the alert currently shown is the standard limit-reached alert (the only one that can be dismissed per page).
  let isLimitReachedAlertShown = false
  const state = ref<AlertModalData>({
    show: false,
    key: '',
    count: 0,
    message: '',
    title: '',
    subtitle: '',
    description: '',
    showBackdrop: false,
  })

  // Load and cache package features once per session.
  function loadPackageFeatures(): void {
    if (packageFeatures.value.length > 0) {
      return
    }

    const userStore = useUserStore()
    const packages = userStore.getTrailPackageFeatures
    Object.values(packages).forEach((pack: any) => {
      pack.features?.forEach((feature: FeatureConfig) => {
        packageFeatures.value.push({
          ...feature,
          package_name: pack.package_name,
          product_name: pack.product_name,
          start_date: pack.start_date,
          renewal_date: pack.renewal_date,
          subscription_type: pack.subscription_type
        })
      })
    })
  }

  // Exposed helper to fetch a feature config by key.
  function getFeatureByKey(key: string): FeatureWithPackageMeta | undefined {
    if (!key) {
      return undefined
    }

    loadPackageFeatures()
    return packageFeatures.value.find((feature: FeatureWithPackageMeta) => feature.feature_key === key)
  }

  // Returns the current modal payload.
  function getState(): AlertModalData {
    return state.value
  }

  function showAlert(
    key: string = '', 
    count: number = 0, 
    message?: string, 
    hidePopup: boolean = false,
    title?: string,
    subtitle?: string,
    description?: string,
    showBackdrop?: boolean
  ): boolean {
    if (state.value.show && !key) {
      return true
    }

    // Backdrop is removed only for the standard feature-limit / free-trial popup.
    // Alerts with custom content keep the backdrop unless the caller says otherwise.
    const isCustomAlert = !!(title || subtitle || description || message)
    const resolvedShowBackdrop = showBackdrop ?? isCustomAlert

    const userStore = useUserStore()
    const isfreeTrailAccount = userStore.getIsTrailAccount;

    const alertTitle = isfreeTrailAccount ? FREE_TRIAL_ALERT.title : FEATURE_LIMIT_REACHED_ALERT.title;
    const alertSubtitle = isfreeTrailAccount ? FREE_TRIAL_ALERT.subtitle : FEATURE_LIMIT_REACHED_ALERT.subtitle;
    const alertDescription = isfreeTrailAccount ? FREE_TRIAL_ALERT.description : FEATURE_LIMIT_REACHED_ALERT.description;

    // The standard limit-reached alert (trial or paid) stays hidden on a page once the user dismissed it in this tab session.
    // The return value is unchanged so callers still know the limit was reached.
    const isLimitReachedAlert = !isCustomAlert
    if (isLimitReachedAlert && getDismissedPages().includes(getCurrentPage())) {
      hidePopup = true
    }
    isLimitReachedAlertShown = isLimitReachedAlert && !hidePopup

    // If no key provided, skip validation and just show the modal
    if (!key) {
      state.value.key = ''
      state.value.count = 0
      state.value.message = message || ''
      state.value.title = title || alertTitle
      state.value.subtitle = subtitle || alertSubtitle
      state.value.description = description || message || alertDescription
      state.value.showBackdrop = resolvedShowBackdrop
      state.value.show = !hidePopup
      return true
    }

    // Validate usage against the package limit when key is provided.
    loadPackageFeatures()
    try {
      const feature = getFeatureByKey(key)
      
      if(!feature || feature.value_type !== 'limit') {
        return false
      }
      
      if(count < Number(feature.value) || Number(feature.value) === -1) {
        return false // limit not reached
      }
      
      // Limit reached - show modal
      state.value.key = key
      state.value.count = count
      state.value.message = message || ''
      state.value.title = title || alertTitle
      state.value.subtitle = subtitle || alertSubtitle
      state.value.description = description || message || alertDescription
      state.value.showBackdrop = resolvedShowBackdrop
      state.value.show = !hidePopup
      
      return true
    } catch (error) {
      console.error('Error showing modal:', error)
      return false
    }
  }

  // Called when the user clicks "Dismiss": hides the alert and remembers the page for this tab session.
  function dismiss(): void {
    if (isLimitReachedAlertShown) {
      const page = getCurrentPage()
      const pages = getDismissedPages()
      if (!pages.includes(page)) {
        try {
          sessionStorage.setItem(DISMISSED_LIMIT_ALERT_PAGES_KEY, JSON.stringify([...pages, page]))
        } catch {
          // Storage unavailable; the alert simply may reappear.
        }
      }
    }
    hide()
  }

  function hide(): void {
    isLimitReachedAlertShown = false
    state.value.show = false
    state.value.key = ''
    state.value.count = 0
    state.value.message = ''
    state.value.title = ''
    state.value.subtitle = ''
    state.value.description = ''
    state.value.showBackdrop = false
  }

  function isFeatureAvailable(key: string): boolean {
    const userStore = useUserStore()
    const features = userStore.getPackageFeatures;
    return features.some((feature: FeatureWithPackageMeta) => feature.feature_key === key)
  }

  return {
    hide,
    dismiss,
    showAlert,
    getState,
    getFeatureByKey,
    isFeatureAvailable,
    state
  };
}

export const alertModalService = AlertModalService();
